import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/u6f2dm1t_CompliSun.png';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!email.endsWith('@complisun.com')) {
      toast.error('Only @complisun.com email addresses are allowed');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      toast.success('Welcome to CompliSun Ops Hub!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A2B5E] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and header */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="CompliSun" className="w-20 h-20 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white font-['Amaranth']">Join CompliCrew</h1>
          <p className="text-white/70 mt-2">Create your Ops Hub account</p>
        </div>

        {/* Register form */}
        <div className="bg-white rounded-xl p-8 shadow-xl" data-testid="register-form">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@complisun.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                className="h-11"
              />
              <p className="text-xs text-navy/50">Only @complisun.com addresses are allowed</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="register-confirm-password-input"
                className="h-11"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90 font-medium"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-navy/60">
              Already have an account?{' '}
              <Link to="/login" className="text-[#106EFD] hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* ISM Quote */}
        <p className="text-center text-white/50 text-sm mt-8 italic">
          "Obsessed with Finding a Better Way"
        </p>
      </div>
    </div>
  );
};

export default Register;
